// src/sectors/pilates/staff/modal/staffStatusModal/ConfirmStaffStatusModal.jsx
import React from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Divider from '@mui/material/Divider';
import Icon from '@mui/material/Icon';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme as useMuiTheme } from '@mui/material/styles';

import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';

export function ConfirmStaffStatusModal({ open, staff, onClose, onConfirm }) {
  const theme = useMuiTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const hasLogin = Boolean(staff?.hasLoginAccount || staff?.username);
  const [loading, setLoading] = React.useState(false);
  const [err, setErr] = React.useState(null);

  React.useEffect(() => {
    if (!open) return;
    setLoading(false);
    setErr(null);
  }, [open]);

  if (!open) return null;

  const isActive = Boolean(staff?.active);
  const title = isActive ? 'Eğitmen İşten Çıkar' : 'Eğitmen Aktifleştir';
  const subtitle = isActive
    ? `Bu işlem Eğitmen pasife alır. İsterse daha sonra tekrar aktifleştirebilirsin.${
        hasLogin ? ' Ayrıca Eğitmenn kullanıcı girişi de otomatik kapatılacak.' : ''
      }`
    : 'Bu işlem Eğitmen tekrar aktif hale getirir.';

  const confirmLabel = isActive ? 'Evet, Pasife Al' : 'Evet, Aktifleştir';
  const confirmColor = isActive ? 'error' : 'success';
  const icon = isActive ? 'person_off' : 'check_circle';

  const doConfirm = async () => {
    setErr(null);
    try {
      setLoading(true);
      await onConfirm?.(staff);
      onClose?.();
    } catch (e) {
      setErr(e?.message || 'İşlem başarısız.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      fullScreen={isMobile}
      fullWidth
      maxWidth="xs"
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : '22px',
          border: isMobile ? 'none' : `1px solid ${theme.palette.divider}`,
          overflow: 'hidden',
          boxShadow: theme.shadows[12],
        },
      }}
    >
      <DialogTitle
        sx={{
          p: 0,
          background: 'linear-gradient(180deg, rgba(255,72,72,.16), rgba(255,72,72,.06))',
        }}
      >
        <MDBox sx={{ px: 2, py: 1.6 }}>
          <MDBox display="flex" alignItems="center" justifyContent="space-between" gap={1}>
            <MDBox display="flex" alignItems="center" gap={1}>
              <MDBox
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: '16px',
                  display: 'grid',
                  placeItems: 'center',
                  background: 'rgba(255,72,72,.14)',
                  border: `1px solid ${theme.palette.divider}`,
                }}
              >
                <Icon sx={{ fontSize: 22 }}>{icon}</Icon>
              </MDBox>

              <MDBox>
                <MDTypography variant="h6" fontWeight="bold" sx={{ lineHeight: 1.1 }}>
                  {title}
                </MDTypography>
                <MDTypography variant="caption" sx={{ color: 'text.secondary' }}>
                  {staff?.fullName ? staff.fullName : 'Seçili eğitmen'}
                </MDTypography>
              </MDBox>
            </MDBox>

            <MDButton
              variant="outlined"
              color="dark"
              onClick={onClose}
              disabled={loading}
              sx={{ borderRadius: '14px' }}
            >
              Kapat
            </MDButton>
          </MDBox>
        </MDBox>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ p: 2 }}>
        <MDTypography variant="button" sx={{ color: theme.palette.text.primary, opacity: 0.95 }}>
          {subtitle}
        </MDTypography>

        <MDBox
          sx={{ mt: 1.25, p: 1.25, borderRadius: 2, border: `1px dashed ${theme.palette.divider}` }}
        >
          <MDTypography variant="caption" sx={{ color: 'text.secondary' }}>
            Emin misin?
          </MDTypography>
        </MDBox>

        {err ? (
          <MDBox
            sx={{
              mt: 1.25,
              p: 1.25,
              borderRadius: 2,
              border: `1px solid ${theme.palette.error.main}`,
              color: theme.palette.error.main,
            }}
          >
            {err}
          </MDBox>
        ) : null}
      </DialogContent>

      <Divider />

      <DialogActions sx={{ p: 2 }}>
        <MDButton
          variant="outlined"
          color="dark"
          sx={{ borderRadius: '14px' }}
          onClick={onClose}
          disabled={loading}
        >
          Vazgeç
        </MDButton>
        <MDButton
          variant="gradient"
          color={confirmColor}
          sx={{ borderRadius: '14px' }}
          onClick={doConfirm}
          disabled={loading}
        >
          <Icon sx={{ mr: 0.5, fontSize: 18 }}>check</Icon>
          {confirmLabel}
        </MDButton>
      </DialogActions>
    </Dialog>
  );
}
